import { useState, useEffect } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { ScrollMaskLine, Reveal } from "./Reveal";
import { ArrowUpRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const IMG = {
  logos:
    "https://images.unsplash.com/photo-1627384113710-424c9181ebbb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBicmFuZGluZyUyMGxvZ28lMjBkZXNpZ24lMjBwb3J0Zm9saW98ZW58MHx8fHwxNzg0MTQyNDg5fDA&ixlib=rb-4.1.0&q=85",
  digital:
    "https://images.unsplash.com/photo-1683721003111-070bcc053d8b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwxfHxkaWdpdGFsJTIwbWFya2V0aW5nJTIwc29jaWFsJTIwbWVkaWElMjBhZ2VuY3l8ZW58MHx8fHwxNzg0MTQyNDg5fDA&ixlib=rb-4.1.0&q=85",
  lab:
    "https://images.unsplash.com/photo-1753505889211-9cfbac527474?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwzfHxjcmVhdGl2ZSUyMGRlc2lnbiUyMHN0dWRpbyUyMGludGVyaW9yfGVufDB8fHx8MTc4NDE0MjQ4OXww&ixlib=rb-4.1.0&q=85",
};

const CATEGORIES = [
  {
    n: "01",
    slug: "logos",
    title: "Logos + Brand Marks",
    desc: "Distinctive identities and marks built to last.",
    img: IMG.logos,
  },
  {
    n: "02",
    slug: "digital",
    title: "Digital Marketing",
    desc: "Social graphics and campaigns that convert.",
    img: IMG.digital,
  },
  {
    n: "03",
    slug: "print",
    title: "Print + Displays",
    desc: "Flyers, banners and large-format that command a room.",
    img: IMG.lab,
  },
  {
    n: "04",
    slug: "email",
    title: "Email + Publications",
    desc: "Templates and editorial layouts with polish.",
    img: IMG.digital,
  },
  {
    n: "05",
    slug: "custom",
    title: "Custom Creative",
    desc: "Bespoke concepts for events and special projects.",
    img: IMG.logos,
  },
  {
    n: "06",
    slug: "portal",
    title: "Portal & Workflow Design",
    desc: "Custom branded systems for submissions, approvals and tracking.",
    img: IMG.digital,
  },
];

export default function Work() {
  const [active, setActive] = useState(null);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const [gallery, setGallery] = useState([]);

  useEffect(() => {
    axios
      .get(`${API}/work`)
      .then(({ data }) => setGallery(data || []))
      .catch(() => setGallery([]));
  }, []);

  const previewSrc = (cat) => {
    const item = gallery.find((g) => g.category === cat.title);
    return item ? `${API}/files/${item.image_path}` : cat.img;
  };

  return (
    <section
      id="work"
      data-testid="work-section"
      className="relative py-28 md:py-40 bg-[#090514]"
      onMouseMove={(e) => setPos({ x: e.clientX, y: e.clientY })}
    >
      <div className="mx-auto max-w-[1500px] px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
          <h2 className="font-display font-extrabold text-white text-4xl sm:text-5xl lg:text-7xl tracking-tight">
            <ScrollMaskLine>Selected</ScrollMaskLine>
            <ScrollMaskLine delay={0.1} className="text-[#C77DFF]">
              Work
            </ScrollMaskLine>
          </h2>
          <Reveal delay={0.2} className="max-w-sm text-[#B5A6CC] md:text-right">
            Six disciplines, one obsessive standard. Every project designed
            with purpose and personality.
          </Reveal>
        </div>

        <div className="border-t border-[#2D1B4E]">
          {CATEGORIES.map((c, i) => (
            <motion.a
              href={`#gallery-${c.slug}`}
              key={c.n}
              data-testid={`work-item-${i}`}
              onMouseEnter={() => setActive(i)}
              onMouseLeave={() => setActive(null)}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.7, delay: i * 0.05 }}
              className="group relative flex items-center justify-between gap-6 border-b border-[#2D1B4E] py-8 md:py-10 transition-colors duration-300"
            >
              <div className="flex items-baseline gap-5 md:gap-10">
                <span className="font-display text-sm md:text-base text-[#6d5b95] tabular-nums">
                  {c.n}
                </span>
                <span className="font-display font-bold text-2xl md:text-5xl text-white group-hover:text-[#C77DFF] group-hover:translate-x-3 transition-all duration-500">
                  {c.title}
                </span>
              </div>
              <div className="flex items-center gap-6">
                <span className="hidden md:block text-sm text-[#B5A6CC] max-w-[220px] text-right opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                  {c.desc}
                </span>
                <span className="grid place-items-center h-11 w-11 rounded-full border border-[#3a2166] text-white group-hover:bg-[#8A2BE2] group-hover:border-[#8A2BE2] transition-colors duration-300">
                  <ArrowUpRight size={18} />
                </span>
              </div>
            </motion.a>
          ))}
        </div>

        {/* Live gallery grouped by discipline */}
        {gallery.length > 0 && (
          <div className="mt-20" data-testid="work-gallery">
            <div className="mb-12 flex items-center gap-3">
              <span className="h-px w-10 bg-[#C77DFF]" />
              <span className="text-xs uppercase tracking-[0.3em] text-[#C77DFF]">
                Featured Projects
              </span>
            </div>

            {CATEGORIES.map((cat) => {
              const items = gallery.filter((g) => g.category === cat.title);
              return (
                <div
                  key={cat.slug}
                  id={`gallery-${cat.slug}`}
                  data-testid={`gallery-group-${cat.slug}`}
                  className="scroll-mt-28 mb-20 last:mb-0"
                >
                  <div className="flex items-baseline justify-between border-b border-[#2D1B4E] pb-4 mb-8">
                    <h3 className="font-display font-bold text-2xl md:text-4xl text-white">
                      {cat.title}
                    </h3>
                    <span className="font-display text-sm text-[#6d5b95] tabular-nums">
                      {String(items.length).padStart(2, "0")}
                    </span>
                  </div>

                  {items.length === 0 ? (
                    <p className="text-[#6d5b95]" data-testid={`gallery-empty-${cat.slug}`}>
                      Selected work coming soon — {cat.desc.toLowerCase()}
                    </p>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                      {items.map((item, i) => (
                        <Reveal
                          key={item.id}
                          delay={(i % 3) * 0.08}
                          data-testid={`gallery-item-${cat.slug}-${i}`}
                          className="group relative overflow-hidden rounded-2xl border border-[#2D1B4E] bg-[#120B24] transition-colors duration-500 hover:border-[#8A2BE2]"
                        >
                          <div className="aspect-[4/3] overflow-hidden bg-[#0d0820] flex items-center justify-center p-6">
                            <img
                              src={`${API}/files/${item.image_path}`}
                              alt={item.title}
                              loading="lazy"
                              className="max-h-full max-w-full object-contain transition-transform duration-700 group-hover:scale-[1.04]"
                            />
                          </div>
                          <div className="border-t border-[#2D1B4E] p-5">
                            <span className="text-xs uppercase tracking-[0.2em] text-[#C77DFF]">
                              {item.category}
                            </span>
                            <h4 className="font-display font-bold text-xl text-white mt-1">
                              {item.title}
                            </h4>
                          </div>
                        </Reveal>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Cursor-follow image preview (desktop) */}
      <AnimatePresence>
        {active !== null && (
          <motion.div
            key={active}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ duration: 0.25 }}
            className="pointer-events-none fixed z-40 hidden lg:block clip-frame bg-[#0d0820] p-3"
            style={{
              left: pos.x + 30,
              top: pos.y - 130,
              width: 300,
              height: 220,
            }}
          >
            <img
              src={previewSrc(CATEGORIES[active])}
              alt={CATEGORIES[active].title}
              className="h-full w-full object-contain"
            />
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
